import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IFatigueOverridePostRequest,
  IFatigueRulePatchRequest,
  IFatigueRulePostRequest,
  IGetAllFatigueOverridesSingleResponse,
  IGetAllFatigueRulesSingleResponse,
  IGetSingleFatigueOverrideResponse,
  IGetSingleFatigueRuleResponse,
} from 'types/api/operationsFatigueApi.interface'
import { IFatigueOverrideFormState, IFatigueSliceInitialState } from 'types/fatigue.interface'
import { IFatigueRuleState, IFatigueRulesSliceInitialState } from 'types/fatigueRules.interface'
import { baseUrl } from 'utils/axios'
import { getParams, handleErrors } from 'utils/helpers'

const mapRuleToState = (data: IGetSingleFatigueRuleResponse): IFatigueRuleState => ({
  id: data.id,
  name: data.name,
  maxHoursPerShift: data.maxHoursPerShift,
  maxHoursPer7Days: data.maxHoursPer7Days,
  minRestHours: data.minRestHours,
  isActive: data.isActive,
})

const fatigueOverridesInitialState: IFatigueSliceInitialState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singleOverride: null,
}

const fatigueRulesInitialState: IFatigueRulesSliceInitialState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singleRule: null,
}

export const getFatigueOverrides = createAsyncThunk(
  'fatigue/getFatigueOverrides',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllFatigueOverridesSingleResponse>> =
        await baseUrl.get('aim-operations/fatigue-override', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleFatigueOverride = createAsyncThunk(
  'fatigue/getSingleFatigueOverride',
  async (id: string, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IGetSingleFatigueOverrideResponse> = await baseUrl.get(
        'aim-operations/fatigue-override/' + id,
      )
      return data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFatigueOverride = createAsyncThunk(
  'fatigue/createFatigueOverride',
  async (state: IFatigueOverrideFormState, thunkAPI) => {
    const req: IFatigueOverridePostRequest = {
      approvedByUserId: state.approvedByUser.value,
      reason: state.reason,
      validUntil: state.validUntil.toISOString(),
    }
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/user/${state.user.value}/fatigue/override`,
        req,
      )
      await thunkAPI.dispatch(getFatigueOverrides(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFatigueRules = createAsyncThunk(
  'fatigue/getFatigueRules',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllFatigueRulesSingleResponse>> =
        await baseUrl.get('aim-operations/fatigue-rule', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleFatigueRule = createAsyncThunk(
  'fatigue/getSingleFatigueRule',
  async (id: string, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IGetSingleFatigueRuleResponse> = await baseUrl.get(
        'aim-operations/fatigue-rule/' + id,
      )
      return mapRuleToState(data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFatigueRule = createAsyncThunk(
  'fatigue/createFatigueRule',
  async (state: IFatigueRuleState, thunkAPI) => {
    const req: IFatigueRulePostRequest = {
      name: state.name,
      maxHoursPerShift: state.maxHoursPerShift,
      maxHoursPer7Days: state.maxHoursPer7Days,
      minRestHours: state.minRestHours,
    }
    try {
      const resp: AxiosResponse = await baseUrl.post('aim-operations/fatigue-rule', req)
      await thunkAPI.dispatch(getFatigueRules(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFatigueRule = createAsyncThunk(
  'fatigue/updateFatigueRule',
  async ({ id, state }: { id: number; state: IFatigueRuleState }, thunkAPI) => {
    const req: IFatigueRulePatchRequest = {
      name: state.name,
      maxHoursPerShift: Number(state.maxHoursPerShift),
      maxHoursPer7Days: Number(state.maxHoursPer7Days),
      minRestHours: Number(state.minRestHours),
      isActive: state.isActive,
    }
    try {
      const resp: AxiosResponse = await baseUrl.patch(`aim-operations/fatigue-rule/${id}`, req)
      await thunkAPI.dispatch(getFatigueRules(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

interface IFatigueState {
  overrides: IFatigueSliceInitialState
  rules: IFatigueRulesSliceInitialState
}

const initialState: IFatigueState = {
  overrides: fatigueOverridesInitialState,
  rules: fatigueRulesInitialState,
}

const fatigueSlice = createSlice({
  name: 'fatigue',
  initialState,
  reducers: {
    clearFatigueOverrides: (state) => {
      state.overrides.data = []
      state.overrides.totalCount = 0
      state.overrides.totalPages = 0
    },
    clearSingleFatigueOverride: (state) => {
      state.overrides.singleOverride = null
    },
    clearFatigueRules: (state) => {
      state.rules.data = []
      state.rules.totalCount = 0
      state.rules.totalPages = 0
    },
    clearSingleFatigueRule: (state) => {
      state.rules.singleRule = null
    },
  },
  extraReducers: {
    [getFatigueOverrides.pending.type]: (state) => {
      state.overrides.isLoading = true
    },
    [getFatigueOverrides.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllFatigueOverridesSingleResponse> },
    ) => {
      state.overrides.isLoading = false
      state.overrides.data = payload.data
      state.overrides.totalCount = payload.totalCount
      state.overrides.totalPages = payload.totalPages
    },
    [getFatigueOverrides.rejected.type]: (state, { payload }) => {
      state.overrides.isLoading = false
      handleErrors(payload)
    },
    [getSingleFatigueOverride.pending.type]: (state) => {
      state.overrides.isLoading = true
    },
    [getSingleFatigueOverride.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSingleFatigueOverrideResponse },
    ) => {
      state.overrides.isLoading = false
      state.overrides.singleOverride = payload
    },
    [getSingleFatigueOverride.rejected.type]: (state, { payload }) => {
      state.overrides.isLoading = false
      handleErrors(payload)
    },
    [createFatigueOverride.pending.type]: (state) => {
      state.overrides.isLoading = true
    },
    [createFatigueOverride.fulfilled.type]: (state) => {
      state.overrides.isLoading = false
      toast.success('Fatigue override created successfully')
    },
    [createFatigueOverride.rejected.type]: (state, { payload }) => {
      state.overrides.isLoading = false
      handleErrors(payload)
    },
    [getFatigueRules.pending.type]: (state) => {
      state.rules.isLoading = true
    },
    [getFatigueRules.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllFatigueRulesSingleResponse> },
    ) => {
      state.rules.isLoading = false
      state.rules.data = payload.data
      state.rules.totalCount = payload.totalCount
      state.rules.totalPages = payload.totalPages
    },
    [getFatigueRules.rejected.type]: (state, { payload }) => {
      state.rules.isLoading = false
      handleErrors(payload)
    },
    [getSingleFatigueRule.pending.type]: (state) => {
      state.rules.isLoading = true
    },
    [getSingleFatigueRule.fulfilled.type]: (state, { payload }: { payload: IFatigueRuleState }) => {
      state.rules.isLoading = false
      state.rules.singleRule = payload
    },
    [getSingleFatigueRule.rejected.type]: (state, { payload }) => {
      state.rules.isLoading = false
      handleErrors(payload)
    },
    [createFatigueRule.pending.type]: (state) => {
      state.rules.isLoading = true
    },
    [createFatigueRule.fulfilled.type]: (state) => {
      state.rules.isLoading = false
      toast.success('Fatigue rule published successfully')
    },
    [createFatigueRule.rejected.type]: (state, { payload }) => {
      state.rules.isLoading = false
      handleErrors(payload)
    },
    [updateFatigueRule.pending.type]: (state) => {
      state.rules.isLoading = true
    },
    [updateFatigueRule.fulfilled.type]: (state) => {
      state.rules.isLoading = false
      toast.success('Fatigue rule updated successfully')
    },
    [updateFatigueRule.rejected.type]: (state, { payload }) => {
      state.rules.isLoading = false
      handleErrors(payload)
    },
  },
})

export const {
  clearFatigueOverrides,
  clearSingleFatigueOverride,
  clearFatigueRules,
  clearSingleFatigueRule,
} = fatigueSlice.actions

export default fatigueSlice.reducer
